import { useMemo, useState } from "react";

import type { Option } from "@sorcery/db";
import {
  ADVANCED_FILTER_TYPES,
  ComparitorEnum,
  ComparitorFilterTypeEnum,
  FilterTypeEnum,
} from "@sorcery/db";
import {
  Button,
  Input,
  Line,
  ModalBody,
  ModalHeader,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@sorcery/ui";

import type { AdvancedSearchPayload } from "~/providers/ModalProvider";
import { useModal } from "~/providers/ModalProvider";
import {
  comparitorOptions,
  keywordOptions,
  subtypeOptions,
  thresholdOptions,
} from "~/utils/filters";
import { api } from "~/utils/trpc";

export const AdvancedCardSearchModal = ({
  filters,
  onConfirm,
}: {
  filters: Option[];
  onConfirm: (payload: AdvancedSearchPayload) => void;
}) => {
  const { closeModal } = useModal();
  const { data } = api.card.getArtists.useQuery(undefined, {
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const artistOptions = useMemo(
    () =>
      (data ?? []).map((a) => ({
        label: a.name,
        value: a.slug,
        type: FilterTypeEnum.ARTIST,
      })) satisfies Option[],
    [data],
  );

  const [selectedSubtypeOptions, setSelectedSubtypeOptions] = useState<Option[]>(
    filters.filter((f) => f.type === FilterTypeEnum.SUBTYPE),
  );
  const [selectedKeywordOptions, setSelectedKeywordOptions] = useState<Option[]>(
    filters.filter((f) => f.type === FilterTypeEnum.KEYWORD),
  );
  const [selectedArtistOptions, setSelectedArtistOptions] = useState<Option[]>(
    filters.filter((f) => f.type === FilterTypeEnum.ARTIST),
  );
  const [selectedPower, setSelectedPower] = useState(
    filters.find((f) => f.type === ComparitorFilterTypeEnum.POWER)?.value,
  );
  const [selectedPowerComparitor, setSelectedPowerComparitor] = useState<Option>(
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    comparitorOptions.find(
      (c) =>
        c.value ===
        (filters.find((f) => f.type === ComparitorFilterTypeEnum.POWER)?.comparitor ??
          ComparitorEnum.EQUALS),
    )!,
  );
  const [powerError, setPowerError] = useState<boolean>(false);

  const [selectedCost, setSelectedCost] = useState(
    filters.find((f) => f.type === ComparitorFilterTypeEnum.COST)?.value,
  );
  const [selectedCostComparitor, setSelectedCostComparitor] = useState<Option>(
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    comparitorOptions.find(
      (c) =>
        c.value ===
        (filters.find((f) => f.type === ComparitorFilterTypeEnum.COST)?.comparitor ??
          ComparitorEnum.EQUALS),
    )!,
  );
  const [costError, setCostError] = useState<boolean>(false);

  const [selectedThresholdElement, setSelectedThresholdElement] = useState<Option | undefined>(
    filters.find((f) => f.type === ComparitorFilterTypeEnum.THRESHOLD),
  );
  // type string as it comes out of the input directly
  const [selectedThresholdTargetNumber, setSelectedThresholdTargetNumber] = useState<
    string | undefined
  >(filters.find((f) => f.type === ComparitorFilterTypeEnum.THRESHOLD)?.target);
  const [selectedThresholdComparitor, setSelectedThresholdComparitor] = useState<Option>(
    // biome-ignore lint/style/noNonNullAssertion: <explanation>
    comparitorOptions.find(
      (c) =>
        c.value ===
        (filters.find((f) => f.type === ComparitorFilterTypeEnum.THRESHOLD)?.comparitor ??
          ComparitorEnum.EQUALS),
    )!,
  );
  const [thresholdTargetError, setThresholdTargetError] = useState<boolean>(false);

  const onSearch = () => {
    // Clear errors
    setPowerError(false);
    setCostError(false);

    const payload: AdvancedSearchPayload = {
      newFilters: [...selectedSubtypeOptions, ...selectedKeywordOptions, ...selectedArtistOptions],
    };

    if (selectedPower) {
      // number validation
      if (Number.isNaN(Number(selectedPower))) {
        setPowerError(true);
        return;
      }
      payload.newFilters.push({
        label:
          ComparitorFilterTypeEnum.POWER.charAt(0).toUpperCase() +
          ComparitorFilterTypeEnum.POWER.slice(1),
        value: selectedPower,
        type: ComparitorFilterTypeEnum.POWER,
        comparitor: selectedPowerComparitor.value as ComparitorEnum,
      });
    }

    if (selectedCost) {
      // number validation
      if (Number.isNaN(Number(selectedCost))) {
        setCostError(true);
        return;
      }

      payload.newFilters.push({
        label:
          ComparitorFilterTypeEnum.COST.charAt(0).toUpperCase() +
          ComparitorFilterTypeEnum.COST.slice(1),
        value: selectedCost,
        type: ComparitorFilterTypeEnum.COST,
        comparitor: selectedCostComparitor.value as ComparitorEnum,
      });
    }

    if (selectedThresholdTargetNumber) {
      // number validation
      if (Number.isNaN(Number(selectedThresholdTargetNumber))) {
        setThresholdTargetError(true);
        return;
      }

      payload.newFilters.push({
        label: selectedThresholdElement?.label ?? "Any",
        value: selectedThresholdElement?.value ?? "*",
        type: ComparitorFilterTypeEnum.THRESHOLD,
        comparitor: selectedThresholdComparitor.value as ComparitorEnum,
        target: selectedThresholdTargetNumber,
      });
    }

    // merge with existing filters
    payload.newFilters = [
      ...filters.filter(
        (f) => !ADVANCED_FILTER_TYPES.includes(f.type as FilterTypeEnum | ComparitorFilterTypeEnum),
      ),
      ...payload.newFilters,
    ];
    onConfirm(payload);
    closeModal();
  };

  return (
    <div className="p-6">
      <ModalHeader title="Advanced Search" />

      <ModalBody>
        {/* item */}
        <div className="flex items-center flex-nowrap pb-2">
          <span className="w-24">Subtypes</span>
          <span className="w-full">
            <MultiSelect
              options={subtypeOptions}
              defaultValue={selectedSubtypeOptions}
              arrow={true}
              onSelect={(options) => {
                setSelectedSubtypeOptions(options);
              }}
              placeholder="Any"
            />
          </span>
        </div>

        {/* item */}
        <div className="flex items-center flex-nowrap pb-2">
          <span className="w-24">Keywords</span>
          <span className="w-full">
            <MultiSelect
              options={keywordOptions}
              defaultValue={selectedKeywordOptions}
              arrow={true}
              onSelect={(options) => {
                setSelectedKeywordOptions(options);
              }}
              placeholder="Any"
            />
          </span>
        </div>

        {/* item */}
        <div className="flex items-center flex-nowrap pb-2">
          <span className="w-24">Artists</span>
          <span className="w-full">
            <MultiSelect
              options={artistOptions}
              defaultValue={selectedArtistOptions}
              arrow={true}
              onSelect={(options) => {
                setSelectedArtistOptions(options);
              }}
              placeholder="Any"
            />
          </span>
        </div>

        {/* item */}

        <div className="flex items-center flex-nowrap pb-2">
          <span className="w-28 sm:w-24">Power</span>

          <span className="mr-1 w-1/2">
            <Select
              onValueChange={(v) =>
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                setSelectedPowerComparitor(comparitorOptions.find((o) => o.value === v)!)
              }
            >
              <SelectTrigger arrow={true}>
                <SelectValue
                  placeholder={
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    comparitorOptions.find((s) => s.value === selectedPowerComparitor.value)!.label
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {comparitorOptions.map((c) => (
                  <SelectItem key={c.label} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>
          <span className="-mb-2 w-1/2">
            <Input
              value={selectedPower}
              onChange={(v) => setSelectedPower(v)}
              error={powerError}
              type="number"
              min={0}
            />
          </span>
        </div>

        {/* item */}

        <div className="flex items-center pb-2">
          <span className="w-28 sm:w-24">Cost</span>

          <span className="mr-1 w-1/2">
            <Select
              onValueChange={(v) =>
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                setSelectedCostComparitor(comparitorOptions.find((o) => o.value === v)!)
              }
            >
              <SelectTrigger arrow={true}>
                <SelectValue
                  placeholder={
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    comparitorOptions.find((s) => s.value === selectedCostComparitor.value)!.label
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {comparitorOptions.map((c) => (
                  <SelectItem key={c.label} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>
          <span className="-mb-2 w-1/2">
            <Input
              value={selectedCost}
              onChange={(v) => setSelectedCost(v)}
              error={costError}
              type="number"
              min={0}
            />
          </span>
        </div>

        {/* item */}

        <div className="flex items-center pb-2">
          <span className="w-[5.4rem]">Threshold</span>

          <span className="mr-1 w-1/3">
            {/* threshold element */}
            <Select
              onValueChange={(v) =>
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                setSelectedThresholdElement(thresholdOptions.find((o) => o.value === v)!)
              }
            >
              <SelectTrigger arrow={true}>
                <SelectValue
                  placeholder={
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    thresholdOptions.find(
                      (s) => s.value === (selectedThresholdElement?.value ?? "*"),
                    )!.label
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {thresholdOptions.map((c) => (
                  <SelectItem key={c.label} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>

          {/* threshold comparitor */}
          <span className="mr-1 w-1/3">
            <Select
              onValueChange={(v) =>
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                setSelectedThresholdComparitor(comparitorOptions.find((o) => o.value === v)!)
              }
            >
              <SelectTrigger arrow={true}>
                <SelectValue
                  placeholder={
                    // biome-ignore lint/style/noNonNullAssertion: <explanation>
                    comparitorOptions.find((s) => s.value === selectedThresholdComparitor.value)!
                      .label
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {comparitorOptions.map((c) => (
                  <SelectItem key={c.label} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>

          <span className="-mb-2 w-20 overflow-hidden">
            <Input
              value={selectedThresholdTargetNumber}
              onChange={(v) => setSelectedThresholdTargetNumber(v)}
              error={thresholdTargetError}
              type="number"
              min={0}
            />
          </span>
        </div>
      </ModalBody>

      {/* footer */}
      <Line />
      <div className="flex flex-row-reverse pt-4 gap-2">
        <Button onClick={onSearch}>Search</Button>
        <Button onClick={closeModal} variant="secondary">
          Cancel
        </Button>
      </div>
    </div>
  );
};
